import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import api from "../../../../api/api";

const MAX_IMAGES = 8;
const MAX_BYTES = 5 * 1024 * 1024;

export function normalizeAssetImages(images) {
  if (Array.isArray(images)) {
    return images
      .map((value) => (typeof value === "string" ? value : value?.url))
      .filter(Boolean);
  }
  if (images && typeof images === "object") {
    return Object.values(images)
      .map((value) => (typeof value === "string" ? value : value?.url))
      .filter(Boolean);
  }
  return [];
}

export default function AssetPhotos({ images, onChange }) {
  const inputRef = useRef(null);
  const normalized = useMemo(() => normalizeAssetImages(images), [images]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const activeIndex = Math.min(selectedIndex, Math.max(0, normalized.length - 1));
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const upload = async (event) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = "";
    setError("");
    if (!selected.length) return;
    if (normalized.length + selected.length > MAX_IMAGES) {
      setError(`У одного актива может быть не более ${MAX_IMAGES} фотографий`);
      return;
    }
    if (selected.some((file) => !["image/jpeg", "image/png", "image/webp"].includes(file.type))) {
      setError("Допустимы только фотографии JPG, PNG и WebP");
      return;
    }
    if (selected.some((file) => file.size > MAX_BYTES)) {
      setError("Размер одной фотографии не должен превышать 5 МБ");
      return;
    }

    const form = new FormData();
    selected.forEach((file) => form.append("files", file));
    try {
      setUploading(true);
      const response = await api.post("/assets/rental/images/upload", form);
      onChange([...normalized, ...(response.data?.images || [])]);
    } catch (uploadError) {
      setError(uploadError.response?.data?.detail || "Не удалось загрузить фотографии");
    } finally {
      setUploading(false);
    }
  };

  const move = (index, offset) => {
    const next = [...normalized];
    const [image] = next.splice(index, 1);
    next.splice(index + offset, 0, image);
    onChange(next);
  };

  const remove = (index) => onChange(normalized.filter((_, itemIndex) => itemIndex !== index));

  return (
    <Paper id="asset-photos" className="asset-section asset-photos" elevation={0} sx={{ p: 3, mb: 3 }}>
      <Box className="asset-photo-heading">
        <Box className="asset-section-title"><PhotoLibraryOutlinedIcon color="primary" /><Typography variant="h6" component="h2">Фотографии</Typography></Box>
        <Button variant="outlined" size="small" startIcon={uploading ? <CircularProgress size={16} /> : <AddPhotoAlternateOutlinedIcon />}
          disabled={uploading || normalized.length >= MAX_IMAGES} onClick={() => inputRef.current?.click()}>
          {uploading ? "Загрузка..." : "Загрузить фото"}
        </Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {normalized.length ? <Box className="asset-photo-gallery">
        <Box className="asset-photo-preview">
          <img src={normalized[activeIndex]} alt="Выбранная фотография актива" />
          <span>{activeIndex === 0 ? 'Обложка каталога' : `${activeIndex + 1} из ${normalized.length}`}</span>
        </Box>
        <Box className="asset-photo-thumbnails">
          {normalized.map((url, index) => <Box className={`asset-photo-thumb ${index === activeIndex ? 'is-selected' : ''}`} key={`${url}-${index}`}>
            <button type="button" className="asset-photo-select" aria-label={`Показать фото ${index + 1}`} aria-pressed={index === activeIndex} onClick={() => setSelectedIndex(index)}>
              <img src={url} alt={`Фотография актива ${index + 1}`} />
            </button>
            <Stack direction="row" justifyContent="center">
              <IconButton size="small" aria-label={`Переместить фото ${index + 1} назад`} disabled={index === 0 || uploading} onClick={() => { move(index, -1); setSelectedIndex(index - 1); }}><ArrowBackIcon fontSize="small" /></IconButton>
              <IconButton size="small" aria-label={`Переместить фото ${index + 1} вперёд`} disabled={index === normalized.length - 1 || uploading} onClick={() => { move(index, 1); setSelectedIndex(index + 1); }}><ArrowForwardIcon fontSize="small" /></IconButton>
              <IconButton size="small" color="error" aria-label={`Удалить фото ${index + 1}`} disabled={uploading} onClick={() => remove(index)}><DeleteOutlineIcon fontSize="small" /></IconButton>
            </Stack>
          </Box>)}
          <Button className="asset-photo-add" variant="outlined" disabled={uploading || normalized.length >= MAX_IMAGES} onClick={() => inputRef.current?.click()}>+ Добавить фото</Button>
        </Box>
      </Box> : <button type="button" className="asset-photo-empty" disabled={uploading} onClick={() => inputRef.current?.click()}>
        <AddPhotoAlternateOutlinedIcon />
        <strong>Добавить фотографии</strong>
        <span>Покажите актив и его комплектацию</span>
      </button>}
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontSize: 12 }}>
        Первая фотография — обложка каталога. До {MAX_IMAGES} файлов JPG, PNG или WebP, не более 5 МБ каждый.
      </Typography>
      <input ref={inputRef} hidden type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={upload} />
    </Paper>
  );
}
